// Story 11.4 — Deploy dialog: confirma, opcionalmente version + notes,
// dispara la mutation y emite log lines locales para feedback inmediato.
// Story 11.5 — pre-validation client-side ANTES de llamar al backend; si
// falla, mostramos los top 3 errores en el dialog y abortamos.

import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Rocket } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDeployBot } from '@/hooks/mutations/use-deploy-bot';
import { type BotRowSummary, deploymentsApi } from '@/lib/api-client';
import { diffConfig, isEmpty as isDiffEmpty } from '@/lib/config-diff';
import { isDirty } from '@/lib/editor-tabs-reducer';
import type { ValidationProblem } from '@/lib/editor-types';
import { clientLog, type LogEntry } from '@/lib/event-to-log';
import { useEditorStore } from '@/stores/editor-store';
import { useValidationStore } from '@/stores/validation-store';
import { Modal } from './Modal';

interface Props {
  open: boolean;
  onClose: () => void;
  bot: BotRowSummary | undefined;
  /** Story 11.4 — el caller (EditorPage) inyecta logs locales al panel. */
  pushLocalLog?: (entry: LogEntry) => void;
  /** Story 11.5 — abre el LogPanel filtrado a `[validation]` cuando hay errores. */
  onShowValidationErrors?: () => void;
}

function defaultVersion(): string {
  // P-435 (review 11.4): incluimos un nonce de 4 chars para evitar
  // colisiones de version cuando el user dispara dos deploys en el mismo
  // milisegundo (rapid double-click) — backend de Story 6.5 enforce un
  // unique constraint sobre version y devolvía 409 críptico.
  const nonce = Math.random().toString(36).slice(2, 6);
  return `v-${Date.now()}-${nonce}`;
}

interface PreValidationState {
  status: 'idle' | 'running' | 'failed';
  errors: ValidationProblem[];
}

const INITIAL_PRE: PreValidationState = { status: 'idle', errors: [] };

export function DeployDialog({
  open,
  onClose,
  bot,
  pushLocalLog,
  onShowValidationErrors,
}: Props): React.ReactElement {
  const botId = bot?.id ?? -1;
  const mutation = useDeployBot(botId);
  const setValidationResult = useValidationStore((s) => s.setResult);
  // P-460 (review 11.5): la pre-validación tiene que considerar dirty tabs
  // — sino el client valida el último save y puede dar OK mientras los
  // edits in-progress son inválidos. MVP fix: bloquear Deploy si hay
  // dirty tabs y mostrar inline "Save first" CTA. Merge real (parsear
  // YAML por path y aplicar al config) se hace en Story 11.x cuando exista
  // browser-side `writeVirtualFile` portable; mientras tanto el block es
  // safe-by-default.
  const tabs = useEditorStore((s) => s.tabs);
  const dirtyCount = tabs.filter(isDirty).length;
  const [version, setVersion] = useState(() => defaultVersion());
  const [notes, setNotes] = useState('');
  const [pre, setPre] = useState<PreValidationState>(INITIAL_PRE);

  // Diff preview vs último deployment activo. Sólo se carga cuando el
  // dialog está abierto — sino consumiríamos la query en cada render del
  // editor.
  const { data: lastList } = useQuery({
    queryKey: ['bot', botId, 'deployments', 'last'],
    queryFn: () => deploymentsApi.list(botId, { limit: 1 }),
    enabled: open && botId > 0,
    staleTime: 30_000,
  });
  const lastDeploymentId = lastList?.[0]?.id;
  const { data: lastDetail } = useQuery({
    queryKey: ['bot', botId, 'deployments', lastDeploymentId, 'detail'],
    queryFn: () => deploymentsApi.getDetail(botId, lastDeploymentId as number),
    enabled: open && botId > 0 && typeof lastDeploymentId === 'number',
    staleTime: 60_000,
  });
  const diff = useMemo(() => {
    if (!bot) return null;
    if (!lastDetail) return null;
    return diffConfig(lastDetail.newConfig ?? {}, bot.config ?? {});
  }, [bot, lastDetail]);
  // P-475 (review 11.5): re-entrancy guard. `mutation.isPending` solo se
  // setea tras `mutation.mutate`; entre el click y ese set hay una ventana
  // donde un segundo click pasa el `canDeploy` check. Ref síncrono evita
  // el race.
  const inFlightRef = useRef(false);

  // P-435: resetear el state cada vez que el dialog se abre.
  useEffect(() => {
    if (open) {
      setVersion(defaultVersion());
      setNotes('');
      setPre(INITIAL_PRE);
      inFlightRef.current = false;
    }
  }, [open]);

  async function runPreValidation(): Promise<ValidationProblem[]> {
    if (!bot) return [];
    setPre({ status: 'running', errors: [] });
    try {
      // Dynamic import — `loadAndValidateBotFromConfig` vive en
      // `@revelation-bots/core/validator-browser` (sub-export browser-safe).
      // P-461 (review 11.5): el barrel root re-exporta `nodeFileReader` /
      // `dynamicLoader` que importan `node:fs/promises`/`node:path` y
      // rompen el bundle Vite. La sub-entry expone solo el validador puro.
      const mod = (await import(
        '@revelation-bots/core/validator-browser'
      )) as typeof import('@revelation-bots/core/validator-browser');
      const result = mod.loadAndValidateBotFromConfig(bot.config ?? {});
      if (result.success) {
        setPre({ status: 'idle', errors: [] });
        return [];
      }
      const errors: ValidationProblem[] = result.errors.map((e) => ({
        code: e.code ?? 'DSL_VALIDATION_ERROR',
        message: e.message ?? String(e),
        location: e.location as ValidationProblem['location'],
        suggestion: (e as { suggestion?: string }).suggestion,
      }));
      setPre({ status: 'failed', errors });
      // Story 11.5 — proyecta al validation-store para que el indicador
      // del toolbar también lo muestre.
      setValidationResult(errors, []);
      return errors;
    } catch (err) {
      // Si el dynamic import falla por algún motivo (browser exotic / build),
      // dejamos que el backend valide. NO bloqueamos el deploy por un fail
      // del propio validador.
      const msg = (err as Error).message ?? 'unknown';
      pushLocalLog?.(clientLog('warn', `Pre-validación cliente saltada: ${msg}`, {}));
      setPre({ status: 'idle', errors: [] });
      return [];
    }
  }

  async function handleSubmit(): Promise<void> {
    if (!bot) return;
    // P-475: re-entrancy guard.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    // P-460: bloquear deploy con dirty tabs — el config en DB no refleja
    // los edits in-progress. Mostramos error inline y abortamos.
    if (dirtyCount > 0) {
      setPre({
        status: 'failed',
        errors: [
          {
            code: 'DIRTY_TABS',
            message: `Hay ${dirtyCount} archivo${dirtyCount === 1 ? '' : 's'} con cambios sin guardar. Guarda antes de hacer deploy para que el config validado coincida con lo que ves en el editor.`,
          },
        ],
      });
      pushLocalLog?.(
        clientLog('warn', `Deploy abortado: ${dirtyCount} dirty tab(s).`, { dirtyCount }),
      );
      inFlightRef.current = false;
      return;
    }
    const errors = await runPreValidation();
    if (errors.length > 0) {
      inFlightRef.current = false;
      pushLocalLog?.(
        clientLog(
          'error',
          `Deploy abortado: ${errors.length} error${errors.length === 1 ? '' : 'es'} de validación`,
          { count: errors.length },
        ),
      );
      return;
    }
    pushLocalLog?.(
      clientLog('info', `Deploying ${bot.name} → ${version || '(auto)'}…`, { version, notes }),
    );
    mutation.mutate(
      { version: version || undefined, notes: notes || null },
      {
        onSuccess: ({ deployment }) => {
          pushLocalLog?.(
            clientLog('info', `Deploy ${deployment.version} activo.`, {
              version: deployment.version,
            }),
          );
          inFlightRef.current = false;
          onClose();
        },
        onError: (err) => {
          pushLocalLog?.(clientLog('error', `Deploy falló: ${(err as Error).message}`, {}));
          inFlightRef.current = false;
        },
      },
    );
  }

  const isBusy = pre.status === 'running' || mutation.isPending;
  const canDeploy = !isBusy && !!bot && pre.status !== 'failed';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Deploy"
      description={bot ? `Crea un snapshot del config actual de "${bot.name}".` : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isBusy}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canDeploy}>
            {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            {pre.status === 'running' ? 'Validando…' : 'Deploy'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {diff && (
          <div className="rounded-md border border-border bg-muted/40 p-3 text-xs">
            <div className="mb-1 font-semibold">
              Cambios vs último deploy
              {lastList?.[0]?.version ? (
                <span className="ml-1 font-mono text-[10px] text-muted-foreground">
                  ({lastList[0].version})
                </span>
              ) : null}
            </div>
            {isDiffEmpty(diff) ? (
              <p className="text-muted-foreground">
                Sin cambios desde el último deploy. Vas a crear un snapshot idéntico.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-3">
                  {diff.added.length > 0 && (
                    <span className="text-success">+{diff.added.length} agregadas</span>
                  )}
                  {diff.changed.length > 0 && (
                    <span className="text-amber-500">~{diff.changed.length} modificadas</span>
                  )}
                  {diff.removed.length > 0 && (
                    <span className="text-destructive">-{diff.removed.length} eliminadas</span>
                  )}
                </div>
                <ul className="mt-2 max-h-32 space-y-0.5 overflow-y-auto font-mono text-[10px]">
                  {diff.added.slice(0, 5).map((p) => (
                    <li key={`a-${p}`} className="text-success">
                      + {p}
                    </li>
                  ))}
                  {diff.changed.slice(0, 5).map((p) => (
                    <li key={`c-${p}`} className="text-amber-500">
                      ~ {p}
                    </li>
                  ))}
                  {diff.removed.slice(0, 5).map((p) => (
                    <li key={`r-${p}`} className="text-destructive">
                      - {p}
                    </li>
                  ))}
                </ul>
                {diff.added.length + diff.changed.length + diff.removed.length > 15 && (
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    Mostrando primeros 5 de cada tipo.
                  </p>
                )}
              </>
            )}
          </div>
        )}
        {pre.status === 'failed' && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-destructive">
              <AlertTriangle className="h-4 w-4" aria-hidden />
              {pre.errors.length} error{pre.errors.length === 1 ? '' : 'es'} bloquean el deploy
            </div>
            <ul className="space-y-1 text-xs">
              {pre.errors.slice(0, 3).map((e, i) => (
                <li
                  // biome-ignore lint/suspicious/noArrayIndexKey: stable order, list never reorders.
                  key={`${e.code}-${i}`}
                  className="rounded border-l-2 border-destructive/60 bg-destructive/5 px-2 py-1"
                >
                  <p className="font-mono text-[10px]">{e.code}</p>
                  <p className="text-foreground">{e.message}</p>
                  {e.location?.line != null && (
                    <p className="text-[10px] text-muted-foreground">línea {e.location.line}</p>
                  )}
                </li>
              ))}
            </ul>
            {pre.errors.length > 3 && onShowValidationErrors && (
              <Button
                variant="link"
                size="sm"
                onClick={onShowValidationErrors}
                className="mt-2 px-0 text-xs"
              >
                Ver todos en LogPanel
              </Button>
            )}
          </div>
        )}
        <div>
          <Label htmlFor="deploy-version" className="text-xs">
            Versión
          </Label>
          <Input
            id="deploy-version"
            type="text"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder={defaultVersion()}
          />
          <p className="mt-1 text-[10px] text-muted-foreground">
            Auto-generada si dejás el campo vacío.
          </p>
        </div>
        <div>
          <Label htmlFor="deploy-notes" className="text-xs">
            Notas (opcional)
          </Label>
          <textarea
            id="deploy-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
            placeholder="Ej. Refactor del flow `order` para soportar promos."
          />
        </div>
      </div>
    </Modal>
  );
}
